import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import api from '../services/api';
import { toast } from 'sonner';
import {
  Download,
  FileSpreadsheet,
  Clock,
  PlayCircle,
  RefreshCw,
  ChevronDown,
  DollarSign,
  ShoppingCart,
  Percent,
  Truck,
  Megaphone,
  Receipt,
  TrendingUp,
  AlertTriangle,
  Info
} from 'lucide-react';

const periods = [
  { value: 'last_7_days', label: 'Últimos 7 días' },
  { value: 'last_15_days', label: 'Últimos 15 días' },
  { value: 'last_30_days', label: 'Últimos 30 días' },
  { value: 'last_60_days', label: 'Últimos 60 días' },
];

function MetricCard({ label, value, sublabel, icon: Icon, color = 'green', testId }) {
  const colorMap = {
    green: 'text-emerald-400',
    red: 'text-red-400',
    amber: 'text-amber-400',
    blue: 'text-blue-400',
  };
  const borderMap = {
    green: 'border-emerald-500/20',
    red: 'border-red-500/20',
    amber: 'border-amber-500/20',
    blue: 'border-blue-500/20',
  };
  return (
    <div data-testid={testId} className={`bg-slate-900/50 border ${borderMap[color]} rounded-xl p-4 hover:bg-slate-800/50 transition-colors`}>
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon size={16} className="text-slate-400" />}
        <span className="text-xs text-slate-400 font-medium">{label}</span>
      </div>
      <p className={`text-2xl font-bold ${colorMap[color]}`}>{value}</p>
      {sublabel && <p className="text-xs text-slate-500 mt-1">{sublabel}</p>}
    </div>
  );
}

export default function DashboardPage() {
  const [period, setPeriod] = useState('last_30_days');
  const [showPeriodMenu, setShowPeriodMenu] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastSync, setLastSync] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncStep, setSyncStep] = useState('');

  // Surface the outcome of the Mercado Libre OAuth redirect. The backend sends
  // us back here with ?meli_connected=true or ?error=...&detail=..., and
  // without this the page looks identical whether it worked or failed.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('meli_connected') === 'true') {
      toast.success('Cuenta de Mercado Libre conectada');
    } else if (params.get('error')) {
      const detail = params.get('detail');
      toast.error(detail ? `No se pudo conectar: ${detail}` : 'No se pudo conectar con Mercado Libre');
      console.error('Mercado Libre connect failed:', params.get('error'), detail);
    } else {
      return;
    }
    window.history.replaceState({}, '', window.location.pathname);
  }, []);

  const formatCurrency = (val) => {
    if (val === null || val === undefined) return '$0';
    return '$' + Math.abs(val).toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  const loadMetrics = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/dashboard/meli-metrics?period=${period}`);
      setMetrics(res.data);
    } catch (err) {
      console.error('Error loading metrics:', err);
    } finally {
      setLoading(false);
    }
  }, [period]);

  const loadSyncStatus = useCallback(async () => {
    try {
      const res = await api.get('/sync/metadata');
      setLastSync(res.data);
      return res.data;
    } catch (err) {
      console.error('Error loading sync status:', err);
      return null;
    }
  }, []);

  // Following the sync lives outside handleSync so a sync already running on
  // the server is picked up when the page mounts. The work happens server
  // side; leaving the page never stopped it, but the page used to come back
  // showing nothing at all.
  const followSync = useCallback(async () => {
    setSyncing(true);
    const startedAt = Date.now();
    try {
      while (Date.now() - startedAt < 30 * 60 * 1000) {
        try {
          const { data: meta } = await api.get('/sync/metadata');
          setLastSync(meta);
          if (meta.status === 'running') {
            setSyncStep(meta.step || 'Sincronizando...');
            await new Promise(r => setTimeout(r, 4000));
            continue;
          }
          if (meta.status === 'error') {
            toast.error(meta.detail || 'La sincronización falló');
          } else if (meta.status === 'interrupted') {
            toast.error(meta.detail || 'La sincronización se detuvo antes de terminar');
          } else {
            toast.success('Sincronización completada');
          }
          break;
        } catch {
          // A dropped poll doesn't mean the sync died; keep watching.
          await new Promise(r => setTimeout(r, 4000));
        }
      }
      await loadMetrics();
    } finally {
      setSyncing(false);
      setSyncStep('');
    }
  }, [loadMetrics]);

  useEffect(() => {
    loadMetrics();
    loadSyncStatus().then(meta => {
      if (meta?.status === 'running') followSync();
    });
  }, [loadMetrics, loadSyncStatus, followSync]);

  const handleSync = async () => {
    setSyncing(true);
    setSyncStep('Iniciando...');
    try {
      const { data } = await api.post('/sync/orders');
      toast.success(data.already_running
        ? 'Ya hay una sincronización en curso'
        : 'Sincronización iniciada');
      await followSync();
    } catch (err) {
      setSyncing(false);
      setSyncStep('');
      toast.error(err.response?.data?.detail || 'Error al sincronizar con Mercado Libre');
    }
  };

  const handleExportOrders = async () => {
    try {
      const res = await api.get(`/dashboard/export-orders?period=${period}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `ordenes_${period}.csv`;
      link.click();
      toast.success('Órdenes descargadas');
    } catch {
      toast.error('Error al descargar órdenes');
    }
  };

  const handleExportAnalysis = async () => {
    try {
      const res = await api.get(`/dashboard/export-detailed?period=${period}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `analisis_${period}.csv`;
      link.click();
      toast.success('Análisis exportado');
    } catch {
      toast.error('Error al exportar análisis');
    }
  };

  const selectedPeriodLabel = periods.find(p => p.value === period)?.label || 'Últimos 30 días';

  const meli = metrics?.meli_official || {};
  const costs = metrics?.local_costs || {};
  const breakdown = costs.fees_breakdown || {};
  const opExpenses = metrics?.operating_expenses_breakdown || {};
  const taxes = metrics?.taxes || {};

  // Calculate totals
  const totalCosts = metrics?.total_costs || 0;
  const netProfit = metrics?.net_profit || 0;
  const marginPct = metrics?.net_margin_pct != null ? Number(metrics.net_margin_pct).toFixed(1) : '0.0';

  return (
    <DashboardLayout>
      {/* Top bar with controls */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button data-testid="tutorial-btn" className="flex items-center gap-2 px-3 py-2 border border-slate-700 rounded-lg text-sm text-slate-300 hover:bg-slate-800 transition-colors">
            <PlayCircle size={16} />
            Ver tutorial
          </button>
          <button
            data-testid="sync-btn"
            onClick={handleSync}
            disabled={syncing}
            className="flex items-center gap-2 px-3 py-2 border border-slate-700 rounded-lg text-sm text-slate-300 hover:bg-slate-800 transition-colors disabled:opacity-50"
          >
            <RefreshCw size={16} className={syncing ? 'animate-spin' : ''} />
            {syncing ? (syncStep || 'Sincronizando...') : 'Sincronizar'}
          </button>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="export-orders-btn"
            onClick={handleExportOrders}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition-colors"
          >
            <Download size={16} />
            Descargar Órdenes
          </button>
          <button
            data-testid="export-analysis-btn"
            onClick={handleExportAnalysis}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
          >
            <FileSpreadsheet size={16} />
            Exportar Análisis
          </button>

          {/* Period selector */}
          <div className="relative">
            <button
              data-testid="period-selector"
              onClick={() => setShowPeriodMenu(!showPeriodMenu)}
              className="flex items-center gap-2 px-4 py-2 border border-slate-700 rounded-lg text-sm text-slate-300 hover:bg-slate-800 transition-colors"
            >
              <Clock size={16} />
              {selectedPeriodLabel}
              <ChevronDown size={14} />
            </button>
            {showPeriodMenu && (
              <div className="absolute right-0 mt-1 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-10 w-48">
                {periods.map(p => (
                  <button
                    key={p.value}
                    onClick={() => { setPeriod(p.value); setShowPeriodMenu(false); }}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-700 transition-colors ${
                      period === p.value ? 'text-blue-400 font-medium' : 'text-slate-300'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Sync status */}
      {lastSync && (
        <div className="mb-4 text-xs flex items-center gap-1.5 flex-wrap">
          <Clock size={12} className="text-slate-500" />
          <span className="text-slate-500">
            Última sincronización: {lastSync.last_sync ? new Date(lastSync.last_sync).toLocaleString('es-MX') : 'nunca'}
          </span>
          {syncing && (
            <span className="text-blue-400">· {syncStep || 'Sincronizando...'}</span>
          )}
          {!syncing && lastSync.status === 'interrupted' && (
            <span className="text-amber-400">
              · La anterior se detuvo antes de terminar. Vuelve a sincronizar: continúa donde se quedó.
            </span>
          )}
          {!syncing && lastSync.status === 'error' && (
            <span className="text-red-400">· {lastSync.detail || 'La última sincronización falló'}</span>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        </div>
      ) : (
        <>
          {/* Section: Números reales de tu negocio */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-2.5 h-2.5 bg-emerald-500 rounded-full" />
              <h2 className="text-base font-semibold text-white">Números reales de tu negocio</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
              <MetricCard
                testId="metric-ventas"
                label="Ventas Reales"
                value={formatCurrency(meli.sales_amount)}
                sublabel={`${meli.orders_count || 0} órdenes · monto real`}
                icon={DollarSign}
                color="green"
              />
              <MetricCard
                testId="metric-unidades"
                label="Unidades Vendidas"
                value={meli.units_sold?.toLocaleString() || '0'}
                sublabel="productos"
                icon={ShoppingCart}
                color="green"
              />
              <MetricCard
                testId="metric-comisiones"
                label="Comisiones Mercado Libre"
                value={`-${formatCurrency(costs.commission)}`}
                sublabel="valor real"
                icon={Percent}
                color="red"
              />
              <MetricCard
                testId="metric-envio"
                label="Costo de Envío"
                value={`-${formatCurrency(costs.shipping_cost)}`}
                sublabel="envío asumido por vendedor"
                icon={Truck}
                color="red"
              />
              <MetricCard
                testId="metric-ads"
                label="Inversión Ads"
                value={`-${formatCurrency(metrics?.ads_investment)}`}
                sublabel={metrics?.ads_investment_source === 'ads_spend_campaign_total_fallback' ? 'Respaldo: campañas' : metrics?.ads_investment_source === 'unavailable' ? 'Sin datos' : 'Por publicación'}
                icon={Megaphone}
                color="red"
              />
            </div>
          </div>

          {/* Impuestos estimados; la retención de ML no se vuelve a cobrar */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <MetricCard
              testId="metric-retenciones"
              label="Ya retenido por ML (se acredita)"
              value={formatCurrency(taxes.retenciones_ml ?? costs.retention_fee)}
              sublabel="No se resta otra vez"
              icon={Receipt}
              color="blue"
            />
            <MetricCard
              testId="metric-iva-neto"
              label="IVA neto"
              value={`-${formatCurrency(taxes.iva_neto)}`}
              sublabel="a cargo estimado"
              icon={Percent}
              color="red"
            />
            <MetricCard
              testId="metric-isr"
              label="ISR"
              value={`-${formatCurrency(taxes.isr)}`}
              sublabel="sobre base configurada"
              icon={Percent}
              color="red"
            />
            <MetricCard
              testId="metric-por-pagar-sat"
              label="Por pagar al SAT"
              value={formatCurrency(taxes.por_pagar_sat)}
              sublabel={taxes.saldo_a_favor_estimado > 0 ? `Saldo a favor estimado: ${formatCurrency(taxes.saldo_a_favor_estimado)}` : 'Después de retenciones ML'}
              icon={Receipt}
              color="amber"
            />
            <MetricCard
              testId="metric-ticket"
              label="Ticket Promedio"
              value={formatCurrency(meli.average_ticket)}
              sublabel="por orden"
              icon={DollarSign}
              color="blue"
            />
            <MetricCard
              testId="metric-visitas"
              label="Visitas"
              value={(metrics?.visits?.total_60d || 0).toLocaleString('es-MX')}
              sublabel="últimos 60 días"
              icon={TrendingUp}
              color="blue"
            />
            <MetricCard
              testId="metric-conversion"
              label="Conversión"
              value={`${(metrics?.visits?.conversion_rate_pct || 0).toFixed(2)}%`}
              sublabel="órdenes por visita"
              icon={Percent}
              color="blue"
            />
          </div>

          {/* Section: Costos Operativos */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 bg-amber-500 rounded-full" />
                <h2 className="text-base font-semibold text-white">Costos Operativos (Propios)</h2>
              </div>
              <a href="/cost" data-testid="manage-costs-link" className="text-sm text-blue-400 hover:text-blue-300 transition-colors">
                Gestionar Costos →
              </a>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <MetricCard
                testId="metric-cogs"
                label={`COGS (Costo de Producto${metrics?.local_costs?.cogs_iva_included ? ' sin IVA' : ''})`}
                value={`-${formatCurrency(costs.cogs)}`}
                icon={Receipt}
                color="amber"
              />
              <MetricCard
                testId="metric-gastos-op"
                label="Gastos Operativos"
                value={formatCurrency(metrics?.operating_expenses)}
                sublabel={`Fijos: ${formatCurrency(opExpenses.fixed)} · Variables: ${formatCurrency(opExpenses.variable)}`}
                icon={Receipt}
                color="amber"
              />
            </div>
          </div>

          {/* Section: Rentabilidad Neta Real */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-2.5 h-2.5 bg-amber-500 rounded-full" />
              <h2 className="text-base font-semibold text-white">Rentabilidad Neta Real</h2>
            </div>
            <div data-testid="profitability-summary" className="bg-gradient-to-r from-slate-900 via-slate-900 to-slate-800/50 border border-slate-700/50 rounded-xl p-6">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-6 flex-wrap">
                  <div>
                    <p className="text-xs text-slate-400 mb-1">Ventas Reales</p>
                    <p className="text-xl font-bold text-emerald-400">{formatCurrency(meli.sales_amount)}</p>
                  </div>
                  <span className="text-2xl text-slate-600">-</span>
                  <div>
                    <p className="text-xs text-slate-400 mb-1">Total Costos</p>
                    <p className="text-xl font-bold text-red-400">-{formatCurrency(totalCosts)}</p>
                  </div>
                  <span className="text-2xl text-slate-600">=</span>
                  <div>
                    <p className="text-xs text-slate-400 mb-1">UTILIDAD NETA</p>
                    <p className="text-xl font-bold text-emerald-400">{formatCurrency(netProfit)}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-slate-400 mb-1">% Margen Neto</p>
                  <p className={`text-3xl font-bold ${parseFloat(marginPct) >= 30 ? 'text-emerald-400' : parseFloat(marginPct) >= 15 ? 'text-amber-400' : 'text-red-400'}`}>
                    {marginPct}%
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom row: Desglose de Costos + Info del Periodo */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            {/* Desglose de Costos */}
            <div data-testid="costs-breakdown" className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-white mb-4">Desglose de Costos</h3>
              <div className="space-y-3">
                {[
                  { label: 'Comisiones ML', value: costs.commission, pct: meli.sales_amount ? ((costs.commission / meli.sales_amount) * 100).toFixed(1) : '0' },
                  { label: 'Costo Envío', value: costs.shipping_cost, pct: meli.sales_amount ? ((costs.shipping_cost / meli.sales_amount) * 100).toFixed(1) : '0' },
                  { label: 'Inversión Publicidad', value: metrics?.ads_investment, pct: meli.sales_amount ? ((metrics?.ads_investment / meli.sales_amount) * 100).toFixed(1) : '0' },
                  { label: 'Gastos Operativos', value: metrics?.operating_expenses, pct: meli.sales_amount ? ((metrics?.operating_expenses / meli.sales_amount) * 100).toFixed(1) : '0' },
                  { label: 'Impuestos del periodo', value: taxes.total, pct: meli.sales_amount ? ((taxes.total / meli.sales_amount) * 100).toFixed(1) : '0' },
                ].map(item => (
                  <div key={item.label} className="flex items-center justify-between text-sm">
                    <span className="text-slate-400">{item.label}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-white font-medium">{formatCurrency(item.value)}</span>
                      <span className="text-xs text-slate-500">({item.pct}%)</span>
                    </div>
                  </div>
                ))}
                <div className="border-t border-slate-700/50 pt-3 flex items-center justify-between text-sm font-semibold">
                  <span className="text-slate-300">TOTAL COSTOS</span>
                  <span className="text-red-400">{formatCurrency(totalCosts)}</span>
                </div>
              </div>
            </div>

            <div id="impuestos" data-testid="taxes-breakdown" className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-white mb-3">Impuestos del periodo (estimación)</h3>
              <p className="text-xs text-slate-400 mb-3">Ventas con IVA incluido · base ISR: {formatCurrency(taxes.isr_base)} (ventas sin IVA)</p>
              <div className="flex justify-between text-sm py-1"><span className="text-slate-400">IVA trasladado ({taxes.iva_rate || 0}%)</span><span className="text-slate-200">{formatCurrency(taxes.iva_trasladado)}</span></div>
              <div className="flex justify-between text-sm py-1"><span className="text-slate-400">IVA acreditable</span><span className="text-emerald-400">-{formatCurrency(taxes.iva_acreditable)}</span></div>
              <div className="flex justify-between text-sm py-1"><span className="text-slate-400">IVA neto a cargo</span><span className="text-red-400">-{formatCurrency(taxes.iva_neto)}</span></div>
              <div className="flex justify-between text-sm py-1"><span className="text-slate-400">ISR ({taxes.isr_rate || 0}%)</span><span className="text-red-400">-{formatCurrency(taxes.isr)}</span></div>
              <div className="flex justify-between text-sm py-1"><span className="text-slate-400">Ya retenido por ML (se acredita)</span><span className="text-blue-400">{formatCurrency(taxes.retenciones_ml)}</span></div>
              <div className="border-t border-slate-700/50 mt-2 pt-2 flex justify-between text-sm font-semibold"><span>Por pagar al SAT</span><span className="text-amber-400">{formatCurrency(taxes.por_pagar_sat)}</span></div>
              {taxes.saldo_a_favor_estimado > 0 && <div className="flex justify-between text-sm py-1"><span className="text-slate-400">Saldo a favor estimado</span><span className="text-emerald-400">{formatCurrency(taxes.saldo_a_favor_estimado)}</span></div>}
              <p className="text-xs text-slate-500 mt-3">Estimación; no sustituye el cálculo del contador. Las retenciones de Mercado Libre son pagos anticipados y no se restan otra vez de la utilidad.</p>
            </div>

            {/* Info del Periodo */}
            <div data-testid="period-info" className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-white mb-4">Información del Período</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-400">Período</span>
                  <span className="text-white">{selectedPeriodLabel}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Desde</span>
                  <span className="text-white">{metrics?.date_from ? new Date(metrics.date_from).toLocaleDateString('es-MX') : '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Hasta</span>
                  <span className="text-white">{metrics?.date_to ? new Date(metrics.date_to).toLocaleDateString('es-MX') : '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Moneda</span>
                  <span className="text-white">{metrics?.currency || 'MXN'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Note */}
          <div className="bg-slate-900/30 border border-slate-800/30 rounded-lg p-4 text-xs text-slate-500 flex items-start gap-2">
            <Info size={14} className="mt-0.5 shrink-0" />
            <p>
              Los números de "Ventas Reales" provienen de las órdenes sincronizadas y excluyen devoluciones y cancelaciones.
              Las comisiones son valores reales extraídos de MercadoPago.
            </p>
          </div>
        </>
      )}
    </DashboardLayout>
  );
}
